import { useEffect, useRef } from 'react';

import { Outlet, useLocation } from 'react-router';

import { useAuthStore } from '@/features/auth/auth.store';
import {
  capturePageview,
  identifyUser,
  resetUser,
} from '@/lib/analytics/posthog';

// Pathless route sitting above every layout, so analytics is wired once
// instead of per layout group (base / CMS / console / standalone pages).
const RootLayout = () => {
  const { pathname, search } = useLocation();
  const user = useAuthStore(state => state.user);

  useEffect(() => {
    capturePageview(pathname);
    // `search` is included so filter/tab changes that rewrite the query string
    // still register as a view, matching what `$current_url` reports.
  }, [pathname, search]);

  // Identify once per account: re-sending on every render would burn events,
  // and `reset()` must only fire on an actual sign-out, not on first load
  // where `user` is simply not resolved yet.
  const identifiedId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (user?.id) {
      if (identifiedId.current === user.id) return;
      identifiedId.current = user.id;
      identifyUser(user);
      return;
    }

    if (identifiedId.current) {
      identifiedId.current = undefined;
      resetUser();
    }
  }, [user]);

  return <Outlet />;
};

export default RootLayout;
